/**
 * The activation lifecycle and the next controls (PLAN §5.3): activate for
 * rehearsal or on air, deactivate, reset — each behind a confirmation, since
 * each changes what airs (`lib/playout/safety.ts`: firmly, Cancel focused, for
 * deactivate and reset while a part is on air for real) — and move next ±1 and skip the next piece, which
 * act at once (they change only what is next).
 *
 * Which confirmation is open is this component's own UI state; what a
 * confirmed action means is the view's (`on…` events).
 */
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';
import { confirmationFor } from '../../lib/playout/safety.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Button } from '../ui/button.js';

export type ActivationStatus = 'inactive' | 'active' | 'rehearsal';

export type PlayoutControlsProps = {
  readonly status: ActivationStatus;
  /** The production was created for rehearsal (its default activation). */
  readonly rehearsalProduction: boolean;
  /** `playout.activate` on the studio. */
  readonly canActivate: boolean;
  /** `playout.take` on the studio. */
  readonly canTake: boolean;
  /** A lifecycle command is in flight. */
  readonly pending: boolean;
  readonly onActivate: (rehearsal: boolean) => void;
  readonly onDeactivate: () => void;
  readonly onReset: () => void;
  readonly onMoveNext: (delta: 1 | -1) => void;
  readonly onDisableNextPiece: () => void;
  /** What is on air now (null: nothing): deactivate and reset ask firmly while a part is on air for real. */
  readonly onAirTitle: string | null;
};

/** The lifecycle confirmations never ask about a layer. */
const NOTHING_PLAYING: ReadonlySet<string> = new Set();

type Confirming = 'activate' | 'rehearsal' | 'deactivate' | 'reset' | null;

export function PlayoutControls(props: PlayoutControlsProps) {
  const [confirming, setConfirming] = useState<Confirming>(null);
  const active = props.status !== 'inactive';
  const lifecycle = props.canActivate && !props.pending;
  const confirm = (): void => {
    if (confirming === 'activate') props.onActivate(false);
    else if (confirming === 'rehearsal') props.onActivate(true);
    else if (confirming === 'deactivate') props.onDeactivate();
    else if (confirming === 'reset') props.onReset();
    setConfirming(null);
  };
  const dialog = confirming === null ? null : confirmationFor({ kind: confirming }, { onAirTitle: props.onAirTitle, rehearsal: props.status === 'rehearsal', layersPlaying: NOTHING_PLAYING });
  return (
    <div className="grid gap-3" data-testid="playout-controls">
      <div className="flex flex-wrap gap-2">
        {active ? (
          <>
            <Button variant="outline" size="sm" disabled={!lifecycle} onClick={() => setConfirming('deactivate')} data-testid="deactivate">
              Deactivate
            </Button>
            <Button variant="outline" size="sm" disabled={!lifecycle} onClick={() => setConfirming('reset')} data-testid="reset">
              Reset
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" disabled={!lifecycle} onClick={() => setConfirming('rehearsal')} data-testid="activate-rehearsal">
              {props.rehearsalProduction ? 'Activate (rehearsal)' : 'Rehearse'}
            </Button>
            {props.rehearsalProduction ? null : (
              <Button variant="take" size="sm" disabled={!lifecycle} onClick={() => setConfirming('activate')} data-testid="activate">
                Activate on air
              </Button>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Next</span>
        <Button variant="outline" size="sm" disabled={!active || !props.canTake} onClick={() => props.onMoveNext(-1)} aria-label="Next: one part back" aria-keyshortcuts="Shift+ArrowUp" data-testid="next-back">
          <ChevronUpIcon />
        </Button>
        <Button variant="outline" size="sm" disabled={!active || !props.canTake} onClick={() => props.onMoveNext(1)} aria-label="Next: one part on" aria-keyshortcuts="Shift+ArrowDown" data-testid="next-forward">
          <ChevronDownIcon />
        </Button>
        <Button variant="ghost" size="sm" disabled={!active || !props.canTake} onClick={props.onDisableNextPiece} data-testid="disable-next-piece">
          Skip next piece
        </Button>
      </div>
      {dialog === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirming(null);
          }}
          title={dialog.title}
          description={dialog.description}
          confirmLabel={dialog.confirmLabel}
          tone={dialog.tone}
          pending={false}
          guarded={dialog.guarded}
          onConfirm={confirm}
        />
      )}
    </div>
  );
}
